import { t } from '#lib/i18n/tranlsator.svelte.ts';
import { toAcceptedImage } from '#lib/util/downscale-image.ts';
import { Fetchable } from '#lib/util/fetchable';
import { globalState } from '#lib/util/global-state.svelte.ts';
import { assetApiService, type UploadedAsset } from '#src/services/asset-api.service.ts';

/** The file picker's filter: the images the backend passes to the model
 *  (modelImageTypes in backend/routes/asset_upload.go), plus GIF and SVG,
 *  which toAcceptedImage turns into PNG, and what the extraction service
 *  extracts text from (extraction/app/routes/attachments.py). The text extensions are listed too for
 *  pickers that don't map text/* to them. Paste and drop aren't filtered: the
 *  backend rejects anything else. */
export const ATTACHMENT_ACCEPT = [
	'.png',
	'.jpg',
	'.jpeg',
	'.webp',
	'.gif',
	'.svg',
	'.pdf',
	'.docx',
	'.pptx',
	'.xlsx',
	'text/*',
	'.txt',
	'.md',
	'.csv',
	'.json',
	'.xml'
].join(',');

/** The backend answers a rejected upload with a code and the limit it exceeds
 *  (see backend/routes/asset_upload.go). */
function uploadErrorMessage(error: unknown): string | null {
	// Fetchable resets its error to null at the start of every fetch.
	if (!error) {
		return null;
	}
	let code = '';
	let limit = 0;
	try {
		({ code, limit } = JSON.parse((error as Error).message));
	} catch {
		// Not one of ours: a network error or an unexpected response.
	}
	switch (code) {
		case 'file_too_large':
			return t('chat.attachment.error.fileTooLarge', { limit: Math.round(limit / 1024 / 1024) });
		case 'too_many_pages':
			return t('chat.attachment.error.tooManyPages', { limit });
		case 'too_many_tokens':
			return t('chat.attachment.error.tooLong');
		case 'unsupported_type':
			return t('chat.attachment.error.unsupportedType');
		case 'unreadable':
		case 'extraction_failed':
			return t('chat.attachment.error.unreadable');
		default:
			return t('chat.attachment.error.failed');
	}
}

/**
 * A file attached to the message being written. Each one is uploaded right
 * away, before Send and before a conversation may exist: it starts unbound and
 * joins its conversation when the message carrying it is sent.
 */
export interface PendingAttachment {
	file: File;
	isImage: boolean;
	upload: Fetchable;
	asset: UploadedAsset | null;
}

/** Starts uploading the file. */
export function attachFile(file: File): PendingAttachment {
	// Reactive on its own, so the upload's result shows wherever the item ends up.
	const item = $state<PendingAttachment>({
		file,
		isImage: file.type.startsWith('image/'),
		upload: new Fetchable({ processError: uploadErrorMessage }),
		asset: null
	});
	item.upload.fetch(async () => {
		const prepared = item.isImage ? await toAcceptedImage(file) : file;
		item.asset = await assetApiService.upload(prepared, globalState.instance.id);
	});
	return item;
}

/** Deletes an upload that was removed before sending, rather than leaving it
 *  to the hourly cleanup. A failed delete is harmless: the cleanup still gets it. */
export async function deleteAttachment(item: PendingAttachment) {
	if (!item.asset?.assetId) {
		return;
	}
	try {
		await assetApiService.delete(item.asset.assetId);
	} catch {
		// Left to the cleanup.
	}
}
